import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { lineReveal } from "./anim";
import { profile } from "@/data/resume";

const CV_URL = `${process.env.PUBLIC_URL || ""}/Mihir_Nadkarni_CV.pdf`;
const PORTRAIT = `${process.env.PUBLIC_URL || ""}/mihir.jpeg`;

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const line = (text, i) => (
    <span className="block overflow-hidden">
      <motion.span
        custom={i}
        variants={lineReveal}
        initial="hidden"
        animate="visible"
        className="block"
      >
        {text}
      </motion.span>
    </span>
  );

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-screen items-end overflow-hidden pb-16 pt-32 md:items-center md:pb-0"
    >
      {/* Parallax portrait — clipped, right side */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[46%]"
      >
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={PORTRAIT}
            alt={profile.name}
            className="h-full w-full object-cover object-center opacity-40 grayscale md:opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FBFAF7] via-[#FBFAF7]/40 to-transparent md:from-[#FBFAF7] md:via-[#FBFAF7]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBFAF7] via-transparent to-transparent" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto w-full max-w-[1500px] px-6 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#9A7526]" />
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-[#9A7526]">
            {profile.title} · {profile.location}
          </span>
        </motion.div>

        <h1 className="font-display text-6xl font-light uppercase leading-[0.86] tracking-tight text-[#1A1A18] sm:text-7xl md:text-8xl lg:text-[9.5vw]">
          {line("Mihir", 0)}
          {line("Nadkarni", 1)}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl font-body text-lg font-light leading-relaxed text-[#52514B] md:text-xl"
        >
          {profile.tagline} A $14M portfolio, 12+ years, and a data-led approach
          to keeping enterprise customers for life.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            onClick={() =>
              document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" })
            }
            data-testid="hero-cta-work"
            className="group inline-flex items-center gap-3 rounded-full bg-[#1A1A18] px-7 py-3 font-mono-x text-xs uppercase tracking-[0.2em] text-[#FBFAF7] transition-colors hover:bg-[#9A7526]"
          >
            View the work
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
          <a
            href={profile.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-linkedin"
            className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#6E6D66] underline-offset-8 transition-colors hover:text-[#1A1A18] hover:underline"
          >
            LinkedIn ↗
          </a>
          <a
            href={CV_URL}
            download
            data-testid="hero-cta-cv"
            className="group inline-flex items-center gap-2 font-mono-x text-xs uppercase tracking-[0.2em] text-[#6E6D66] transition-colors hover:text-[#9A7526]"
          >
            <Download size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            Download CV
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 md:block"
      >
        <span className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-[#6E6D66]">
          Scroll
        </span>
      </motion.div>
    </section>
  );
};
